import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Button, FileUpload, Icon, Modal, Radio } from '@ab-inbev-maz/compass';
import type { Canvas } from 'fabric';
import { canvasService } from '@/services/canvasService';
import { useCanvasEditor } from './CanvasEditorContext';
import { renderCanvasPreview } from './canvasPreview';
import type { FlushSave } from '@/hooks/canvas/useCanvasAutosave';
import {
  runCanvasBatch,
  validateBatchFile,
  type BatchValidationIssue,
  type BatchValidationProgress,
} from './canvasBatch';
import {
  buildBatchFormatCsv,
  downloadBlob,
  exportCanvasHtml,
  exportCanvasJson,
  exportGif,
  exportStaticImage,
  getAnimatedObjects,
  playAnimationPreview,
  toExportFileName,
  type ExportImageType,
  type ExportQuality,
} from './canvasExport';

type ExportMode = 'individual' | 'batch';
type ExportFormat = 'image' | 'html' | 'json';
type ImageType = ExportImageType;
type ImageQuality = ExportQuality;

export interface CanvasExportOptions {
  mode: ExportMode;
  format: ExportFormat;
  imageType: ImageType;
  imageQuality: ImageQuality;
  batchFile: File | null;
}

type BatchPhase = 'upload' | 'processing' | 'error' | 'done';

interface CanvasExportModalProps {
  onClose: () => void;
  // Called with the filled file once its rows, and its SKUs, check out.
  onExport?: (options: CanvasExportOptions) => void;
  // Name of the downloaded file.
  title?: string;
  // Needed by the JSON export, which downloads the canvas as the backend has it.
  canvasId?: string;
  flushSave?: FlushSave;
}

const MODE_TABS: { id: ExportMode; label: string }[] = [
  { id: 'individual', label: 'Individual' },
  { id: 'batch', label: 'Masivo (batch)' },
];

const FORMAT_OPTIONS: { id: ExportFormat; label: string; icon: ReactNode }[] = [
  { id: 'image', label: 'Imagen', icon: <Icon name="media/image" /> },
  { id: 'html', label: 'HTML', icon: <Icon name="technology/code" /> },
  { id: 'json', label: 'JSON', icon: <span className="text-base font-semibold leading-none">{'{ }'}</span> },
];

const IMAGE_TYPES: { id: ImageType; label: string }[] = [
  { id: 'png', label: 'PNG' },
  { id: 'jpg', label: 'JPG' },
  { id: 'gif', label: 'GIF' },
];

const IMAGE_QUALITIES: { id: ImageQuality; label: string }[] = [
  { id: 'low', label: 'Baja' },
  { id: 'medium', label: 'Media' },
  { id: 'high', label: 'Alta' },
];

const BATCH_ACCEPTED_TYPES = '.csv,.xlsx';
const BATCH_MAX_SIZE_MB = 5;
const PREVIEW_MAX_SIDE = 600;

const selectableClassName = (selected: boolean) =>
  `cursor-pointer border rounded-lg transition-colors ${
    selected ? 'border-[#FFCC00] bg-[#FFF9E5] text-gray-900' : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
  }`;

const buildPreviewUrl = (canvas: Canvas | null, width: number, height: number): string | null =>
  renderCanvasPreview(canvas, width, height, { maxSide: PREVIEW_MAX_SIDE });

const BatchSpinner = () => (
  <div
    className="h-14 w-14 animate-spin rounded-full border-[5px] border-[#F6E7A8] border-t-[#E6A700]"
    aria-hidden="true"
  />
);

const BatchFolderIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" className="text-gray-500">
    <path
      d="M2 5.5h5l1.5-2H16V14H2V5.5z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
  </svg>
);

const BatchFileIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true" className="text-gray-500">
    <path
      d="M4 2.5h6l4 4V15.5H4V2.5z"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinejoin="round"
    />
    <path d="M10 2.5V6.5h4" fill="none" stroke="currentColor" strokeWidth="1.4" />
  </svg>
);

const BatchInfoMark = () => (
  <svg width="40" height="40" viewBox="0 0 40 40" aria-hidden="true" className="text-blue-600">
    <circle cx="20" cy="20" r="14" fill="none" stroke="currentColor" strokeWidth="2" />
    <circle cx="20" cy="13" r="1.6" fill="currentColor" />
    <path d="M20 18v10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const BatchErrorMark = () => (
  <svg width="56" height="56" viewBox="0 0 56 56" aria-hidden="true" className="text-red-600">
    <path
      d="M20 6h16l14 14v16L36 50H20L6 36V20L20 6z"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinejoin="round"
    />
    <circle cx="28" cy="20" r="1.8" fill="currentColor" />
    <path d="M28 26v12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
  </svg>
);

// Modal to export the canvas. The individual tab exports images (PNG/JPG/GIF), HTML and JSON. The batch
// tab downloads a CSV of the canvas SKUs and texts, checks the filled file, then creates one combo per row.
const CanvasExportModal = ({ onClose, onExport, title = '', canvasId, flushSave }: CanvasExportModalProps) => {
  const { canvas, canvasWidth, canvasHeight } = useCanvasEditor();
  const [mode, setMode] = useState<ExportMode>('individual');
  const [format, setFormat] = useState<ExportFormat>('image');
  const [imageType, setImageType] = useState<ImageType>('png');
  const [imageQuality, setImageQuality] = useState<ImageQuality>('high');
  const [batchFile, setBatchFile] = useState<File | null>(null);
  const [batchPhase, setBatchPhase] = useState<BatchPhase>('upload');
  const [batchProgress, setBatchProgress] = useState<BatchValidationProgress>({ processed: 0, total: 0 });
  const [batchIssues, setBatchIssues] = useState<BatchValidationIssue[]>([]);
  const [batchTaskId, setBatchTaskId] = useState<string | null>(null);
  const [batchCount, setBatchCount] = useState(0);
  const cancelledRef = useRef(false);
  const [uploadKey, setUploadKey] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const [gifProgress, setGifProgress] = useState<number | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const stopPreviewRef = useRef<(() => void) | null>(null);

  const previewUrl = useMemo(() => buildPreviewUrl(canvas, canvasWidth, canvasHeight), [canvas, canvasWidth, canvasHeight]);
  // The editor is blocked while the modal is open, so the animations can't change meanwhile.
  const hasAnimations = useMemo(() => !!canvas && getAnimatedObjects(canvas).length > 0, [canvas]);

  // Plays every animation in the preview, frame by frame as the GIF shows them, then goes back to the image.
  useEffect(() => {
    if (!isPlaying || !canvas || !previewCanvasRef.current) return;
    const multiplier = Math.min(1, PREVIEW_MAX_SIDE / Math.max(canvasWidth, canvasHeight, 1));
    const stop = playAnimationPreview(canvas, previewCanvasRef.current, multiplier, () => setIsPlaying(false));
    stopPreviewRef.current = stop;
    return () => {
      stopPreviewRef.current = null;
      stop();
    };
  }, [isPlaying, canvas, canvasWidth, canvasHeight]);

  const isBatch = mode === 'batch';
  // HTML and JSON read the canvas back from the backend.
  const formatAvailable = format === 'image' ? !!canvas : !!canvasId && !!flushSave;
  const canExport = !isExporting && (isBatch ? batchFile !== null : formatAvailable);

  const handleClose = () => {
    if (!isExporting) onClose();
  };

  // Only called with an available format (the Exportar button is disabled otherwise).
  const exportIndividual = async (): Promise<{ blob: Blob; extension: string }> => {
    if (format === 'json' && canvasId && flushSave) {
      return { blob: await exportCanvasJson(canvasId, flushSave), extension: 'json' };
    }
    if (format === 'html' && canvasId && flushSave) {
      return { blob: await exportCanvasHtml(canvasId, flushSave), extension: 'html' };
    }
    if (format !== 'image' || !canvas) throw new Error('Formato no disponible');
    if (imageType === 'gif') return { blob: await exportGif(canvas, imageQuality, setGifProgress), extension: 'gif' };
    return { blob: await exportStaticImage(canvas, imageType, imageQuality), extension: imageType };
  };

  useEffect(() => () => {
    cancelledRef.current = true;
  }, []);

  const resetBatchUpload = () => {
    setBatchPhase('upload');
    setBatchFile(null);
    setBatchIssues([]);
    setBatchTaskId(null);
    setBatchCount(0);
    setBatchProgress({ processed: 0, total: 0 });
    setUploadKey((key) => key + 1);
  };

  const handleBatchExport = async () => {
    if (!batchFile) return;
    setIsExporting(true);
    setBatchPhase('processing');
    setBatchProgress({ processed: 0, total: 0 });
    setBatchIssues([]);
    setExportError(null);
    try {
      const result = await validateBatchFile(batchFile, canvas, setBatchProgress);
      if (cancelledRef.current) return;
      if (result.unreadable || result.issues.length > 0) {
        setBatchIssues(result.issues);
        setBatchPhase('error');
        return;
      }
      if (!canvasId) {
        setExportError('Guarda el canvas antes de generar los combos.');
        setBatchPhase('upload');
        return;
      }
      const outcome = await runCanvasBatch(canvasId, batchFile, setBatchProgress);
      if (cancelledRef.current) return;
      onExport?.({ mode, format, imageType, imageQuality, batchFile });
      setBatchTaskId(outcome.taskId);
      setBatchCount(outcome.count);
      setBatchPhase('done');
    } catch (error) {
      if (cancelledRef.current) return;
      console.error('Error al generar los combos:', error);
      const message = error instanceof Error ? error.message : 'No se pudieron generar los combos';
      setBatchIssues([{ row: 0, message }]);
      setBatchPhase('error');
    } finally {
      if (!cancelledRef.current) setIsExporting(false);
    }
  };

  const downloadBatchFile = async (kind: 'zip' | 'urls') => {
    if (!batchTaskId) return;
    setExportError(null);
    try {
      const blob = await canvasService.downloadCanvasBatchFile(batchTaskId, kind);
      downloadBlob(blob, kind === 'zip' ? 'combos.zip' : 'combos-urls.csv');
    } catch (error) {
      console.error('Error al descargar los combos:', error);
      setExportError(error instanceof Error ? error.message : 'No se pudo descargar el archivo');
    }
  };

  const handleExport = async () => {
    if (isBatch) {
      await handleBatchExport();
      return;
    }
    // Right away (not on the next render): the export reads the objects the preview is moving.
    stopPreviewRef.current?.();
    setIsExporting(true);
    setGifProgress(null);
    setExportError(null);
    try {
      const { blob, extension } = await exportIndividual();
      downloadBlob(blob, toExportFileName(title, extension));
      onClose();
    } catch (error) {
      console.error('Error al exportar el canvas:', error);
      setExportError(error instanceof Error ? error.message : 'No se pudo exportar el canvas');
    } finally {
      setIsExporting(false);
      setGifProgress(null);
    }
  };

  const exportLabel = () => {
    if (!isExporting) return 'Exportar';
    return gifProgress === null ? 'Exportando...' : `Generando GIF… ${Math.round(gifProgress * 100)}%`;
  };

  const handleBatchFileChange = (event?: React.ChangeEvent<HTMLInputElement>) => {
    setBatchFile(event?.target.files?.[0] ?? null);
  };

  const handleDownloadFormat = () => {
    const csv = buildBatchFormatCsv(canvas);
    const fileName = toExportFileName(title ? `${title} formato` : 'formato', 'csv');
    downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8' }), fileName);
  };

  const renderIndividual = () => (
    <div className="flex">
      <div className="flex flex-col gap-6 w-1/2 shrink-0">
        <div className="grid grid-cols-3 gap-3">
          {FORMAT_OPTIONS.map((option) => (
            <button
              key={option.id}
              type="button"
              aria-pressed={format === option.id}
              onClick={() => setFormat(option.id)}
              className={`flex flex-col items-center justify-center gap-1 h-16 text-sm ${selectableClassName(format === option.id)}`}
            >
              <span className="flex items-center justify-center w-6 h-6 text-2xl">{option.icon}</span>
              {option.label}
            </button>
          ))}
        </div>

        {format === 'image' && (
          <>
            <div className="flex flex-col gap-3">
              <p className="text-sm font-medium text-gray-700">Tipo de imagen</p>
              <div
                className="flex items-center gap-6 [--radio-label-font-size:0.875rem]"
                role="radiogroup"
                aria-label="Tipo de imagen"
              >
                {IMAGE_TYPES.map((type) => (
                  <Radio
                    key={type.id}
                    id={`canvas-export-type-${type.id}`}
                    name="canvas-export-image-type"
                    value={type.id}
                    label={type.label}
                    checked={imageType === type.id}
                    disabled={type.id === 'gif' && !hasAnimations}
                    onChange={() => setImageType(type.id)}
                  />
                ))}
              </div>
              {!hasAnimations && (
                <p className="text-xs text-gray-500">El GIF requiere al menos un elemento con animación.</p>
              )}
            </div>

            <div className="flex flex-col gap-3">
              <p className="text-sm font-medium text-gray-700">Calidad</p>
              <div className="grid grid-cols-3 gap-3">
                {IMAGE_QUALITIES.map((quality) => (
                  <button
                    key={quality.id}
                    type="button"
                    aria-pressed={imageQuality === quality.id}
                    onClick={() => setImageQuality(quality.id)}
                    className={`py-2 text-sm ${selectableClassName(imageQuality === quality.id)}`}
                  >
                    {quality.label}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {format === 'html' && (
          <p className="text-sm text-gray-500">
            Descarga una página HTML con la imagen del canvas enlazada. Los cambios pendientes y la imagen se guardan
            antes.
          </p>
        )}
        {format === 'json' && (
          <p className="text-sm text-gray-500">
            Descarga el canvas completo con su estructura, tal como está guardado. Los cambios pendientes se guardan antes.
          </p>
        )}
      </div>

      <div className="flex flex-col gap-3 w-1/2 min-w-0 pl-6">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-gray-900">Vista previa</p>
          <button
            type="button"
            disabled={!hasAnimations || isExporting}
            title={hasAnimations ? undefined : 'El canvas no tiene animaciones'}
            onClick={() => setIsPlaying((playing) => !playing)}
            className="flex items-center gap-1 text-sm text-gray-600 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isPlaying ? 'Detener' : 'Reproducir'}
            <span className="w-4 h-4 flex items-center justify-center">
              <Icon name={isPlaying ? 'media/pause' : 'media/play'} />
            </span>
          </button>
        </div>
        <div className="flex items-center justify-center rounded-lg bg-gray-100 h-64 p-3">
          {isPlaying ? (
            <canvas
              ref={previewCanvasRef}
              aria-label="Animación del canvas"
              className="max-w-full max-h-full object-contain"
            />
          ) : previewUrl ? (
            <img src={previewUrl} alt="Vista previa del canvas" className="max-w-full max-h-full object-contain" />
          ) : (
            <p className="text-sm text-gray-500">Vista previa no disponible</p>
          )}
        </div>
      </div>
    </div>
  );

  const renderBatchUpload = () => (
    <div className="flex flex-col items-center gap-2 rounded-lg border border-gray-100 bg-white">
      <FileUpload
        key={uploadKey}
        img={
          <span className="flex h-10 w-10">
            <Icon name="file/file-upload" />
          </span>
        }
        label="Arrastra tu archivo CSV o XLSX aquí"
        help={
          <div className="text-xs text-center text-gray-600">
            Solo se permite cargar un archivo a la vez, en formato CSV o XLSX. Otros formatos no serán aceptados. Tamaño máximo: {BATCH_MAX_SIZE_MB} MB.
          </div>
        }
        instructions={
          <>
            <span className="flex h-6 w-6">
              <Icon name="file/file-upload" />
            </span>
            <p className="text-sm font-semibold underline">Selecciona o arrastra aquí tu documento</p>
          </>
        }
        accept={BATCH_ACCEPTED_TYPES}
        maxSize={BATCH_MAX_SIZE_MB * 1024 * 1024}
        maxSizeErrorMessage={`El tamaño del archivo excede el límite de ${BATCH_MAX_SIZE_MB} MB`}
        multiple={false}
        onChange={handleBatchFileChange}
        className="max-w-full! w-full [--file-upload-container-border:none]"
      />
    </div>
  );

  const renderBatchProcessing = () => (
    <div
      role="status"
      aria-live="polite"
      className="flex min-h-80 flex-col items-center justify-center gap-4 rounded-lg border border-gray-100 bg-white"
    >
      <BatchSpinner />
      <div className="text-center">
        <p className="text-base font-semibold text-gray-700">Procesando exportación...</p>
        {batchProgress.total > 0 && (
          <p className="mt-1 text-sm text-gray-500">
            {batchProgress.processed} de {batchProgress.total} registros procesados
          </p>
        )}
      </div>
    </div>
  );

  const renderBatchError = () => (
    <div className="min-h-80 rounded-lg border border-gray-100 bg-white px-8 py-10">
      <div className="flex flex-col items-center gap-2 text-center">
        <BatchErrorMark />
        <h3 className="mt-2 text-lg font-semibold text-gray-900">No se pudo procesar la exportación</h3>
        <p className="max-w-md text-sm text-gray-500">
          El archivo no pudo leerse correctamente. Verifica el formato e inténtalo de nuevo.
        </p>
      </div>
      {batchIssues.length > 0 && (
        <ul className="mx-auto mt-8 max-h-48 columns-2 gap-x-10 overflow-y-auto pl-5 text-left text-sm text-gray-800 list-disc">
          {batchIssues.map((issue, index) => (
            <li key={`${issue.row}-${index}`} className="mb-3 break-inside-avoid">
              {issue.row > 0 && <span className="font-semibold">Fila {issue.row}: </span>}
              {issue.message}
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  const renderBatchDone = () => (
    <div className="flex min-h-80 flex-col items-center justify-center gap-6 rounded-lg border border-gray-100 bg-white px-8 py-10">
      <BatchInfoMark />
      <p className="text-base font-semibold text-gray-700">
        {batchCount} {batchCount === 1 ? 'combo generado' : 'combos generados'}
      </p>
      <div className="flex w-full max-w-md flex-col gap-3">
        <button
          type="button"
          onClick={() => void downloadBatchFile('zip')}
          className="flex items-center justify-between rounded-md border border-gray-300 bg-white px-4 py-3 text-sm text-gray-800 cursor-pointer"
        >
          <span className="flex items-center gap-2">
            <BatchFolderIcon />
            Descargar ZIP
          </span>
          <Icon name="arrow/download" />
        </button>
        <button
          type="button"
          onClick={() => void downloadBatchFile('urls')}
          className="flex items-center justify-between rounded-md border border-gray-300 bg-white px-4 py-3 text-sm text-gray-800 cursor-pointer"
        >
          <span className="flex items-center gap-2">
            <BatchFileIcon />
            Archivo plano con URLs
          </span>
          <Icon name="arrow/download" />
        </button>
      </div>
    </div>
  );

  const renderBatch = () => {
    if (batchPhase === 'processing') return renderBatchProcessing();
    if (batchPhase === 'error') return renderBatchError();
    if (batchPhase === 'done') return renderBatchDone();
    return renderBatchUpload();
  };

  return (
    <Modal open onClose={handleClose}>
      <div
        role="dialog"
        aria-label="Exportar canvas"
        className="flex flex-col gap-4 p-8 bg-zinc-50 rounded-xl shadow-lg w-[48rem] max-w-full mx-auto"
      >
        <h2 className="text-xl font-semibold text-gray-900">Exportar canvas</h2>

        <div className="flex items-center justify-between">
          <div className="flex w-1/2 rounded-lg bg-gray-200 p-0" role="tablist">
            {MODE_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={mode === tab.id}
                onClick={() => {
                  if (!isExporting) setMode(tab.id);
                }}
                className={`flex-1 px-4 py-2 text-sm font-semibold rounded-lg cursor-pointer ${
                  mode === tab.id ? 'bg-[#FFCC00] text-gray-900' : 'text-gray-500'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {isBatch && (
            <div className="flex w-1/2 justify-end">
              <button
                type="button"
                onClick={() => {
                  if (!isExporting) handleDownloadFormat();
                }}
                className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer"
              >
                Descargar formato
                <span className="w-5 h-5 flex items-center justify-center">
                  <Icon name="arrow/download" />
                </span>
              </button>
            </div>
          )}
        </div>

        {/* Same minimum height in both tabs so the modal does not jump when switching. */}
        <div className="min-h-80">{isBatch ? renderBatch() : renderIndividual()}</div>

        {exportError && (
          <p role="alert" className="text-sm text-center text-red-600">
            {exportError}
          </p>
        )}

        {isBatch && batchPhase === 'done' && (
          <div className="flex items-center justify-center">
            <Button
              label="Cerrar"
              variant="secondary"
              className="cursor-pointer rounded-full min-w-40"
              onClick={handleClose}
            />
          </div>
        )}

        {!(isBatch && (batchPhase === 'processing' || batchPhase === 'done')) && (
          <div className="flex items-center justify-center gap-4">
            <Button
              label="Cancelar"
              variant="secondary"
              className="cursor-pointer rounded-full min-w-40"
              disabled={isExporting}
              onClick={handleClose}
            />
            {isBatch && batchPhase === 'error' ? (
              <Button
                label="Subir nuevo archivo"
                variant="primary"
                className="cursor-pointer rounded-full min-w-52"
                onClick={resetBatchUpload}
              />
            ) : (
              <Button
                label={exportLabel()}
                variant="primary"
                className="cursor-pointer rounded-full min-w-40"
                disabled={!canExport}
                onClick={() => void handleExport()}
              />
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default CanvasExportModal;
